import { Checkbox } from '@mui/material';
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import { Visibility as ViewIcon } from '@mui/icons-material';
import React, { useEffect, useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';

import ViewStateManager from '../../utils/viewStateManager';
import { setActiveActivityBox } from '../../store/Activities';
import { getPipelines, moveDealStage, setPipelineBoard } from '../../store/Deals';
import PlanAnimation from '../../components/Animations/PlanAnimation.js';

const PipelineView = ({ selectItems, setSelectItems, setIsSideViewModal, onGetData }) => {

  const history = useHistory();
  const dispatch = useDispatch();
  const { pipelineBoard, loading } = useSelector(state => state['Deals']);

  const [pipeline, setPipeline] = useState([]);
  const [highlightedStage, setHighlightedStage] = useState(null);
  const [isVisibleBlocks, setIsVisibleBlocks] = useState(() => ViewStateManager.getPipelineCollapsedStages());

  useEffect(() => { dispatch(getPipelines()) }, []);
  useEffect(() => { setPipeline(pipelineBoard) }, [pipelineBoard]);

  const onDragStart = useCallback((evt) => {
    let element = evt['currentTarget'];
    element['classList'].add('dragged');
    evt['dataTransfer'].setData('text/plain', element['id']);
    evt['dataTransfer']['effectAllowed'] = 'move';

    // Add global dragging class for cursor
    document.body.classList.add('dragging');
  }, []);

  // const onDragEnd = useCallback((evt) => {
  //   evt['currentTarget']['classList'].remove('dragged');

  //   // Clean up all visual feedback when drag ends
  //   const allContainers = document.querySelectorAll('.drag_row');
  //   allContainers.forEach(container => {
  //     container.classList.remove('drag-active');
  //     const highlights = container.querySelectorAll('.drop-zone.highlight');
  //     highlights.forEach(zone => zone.classList.remove('highlight'));
  //   });
  //   document.body.classList.remove('dragging');
  // }, []);

  const onDragEnter = useCallback((evt) => {
    evt.preventDefault();
    evt['currentTarget']['classList'].add('dragged-over');
    evt['dataTransfer']['dropEffect'] = 'move';

    // Add drag-active class only to this column's drop container
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      dropContainer.classList.add('drag-active');
    }
  }, []);

  const onDragLeave = useCallback((evt) => {
    let newTarget = evt['relatedTarget'];
    let currentTarget = evt['currentTarget'];
    if (newTarget?.['parentNode'] === currentTarget || newTarget === currentTarget) return;
    evt.preventDefault();
    evt['currentTarget']['classList'].remove('dragged-over');

    // Remove drag-active and clear any highlights when leaving the column
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      dropContainer.classList.remove('drag-active');
      const highlights = dropContainer.querySelectorAll('.drop-zone.highlight');
      highlights.forEach(zone => zone.classList.remove('highlight'));
    }
  }, []);

  const onDragOver = useCallback((evt) => {
    evt.preventDefault();
    evt['dataTransfer']['dropEffect'] = 'move';

    // Highlight drop zones based on mouse position
    const dropContainer = evt.currentTarget.querySelector('.drag_row');
    if (dropContainer) {
      const rect = dropContainer.getBoundingClientRect();
      const dropY = evt.clientY - rect.top;

      // Remove all existing highlights
      const existingHighlights = dropContainer.querySelectorAll('.drop-zone.highlight');
      existingHighlights.forEach(zone => zone.classList.remove('highlight'));

      // Find which drop zone to highlight
      const dropZones = Array.from(dropContainer.querySelectorAll('.drop-zone'));
      let closestZone = null;
      let minDistance = Infinity;

      dropZones.forEach(zone => {
        const zoneRect = zone.getBoundingClientRect();
        const zoneY = zoneRect.top + zoneRect.height / 2 - rect.top;
        const distance = Math.abs(dropY - zoneY);

        if (distance < minDistance) {
          minDistance = distance;
          closestZone = zone;
        }
      });

      // Highlight the closest drop zone
      if (closestZone && minDistance < 50) return closestZone.classList.add('highlight'); // Within 50px threshold
    }
  }, []);

  // const onDrop = useCallback((evt, _, newStage) => {
  //   evt.preventDefault();
  //   evt['currentTarget']['classList'].remove('dragged-over');

  //   const data = evt['dataTransfer']['getData']('text/plain');

  //   let dealData, prevStage;
  //   for (let stage of pipeline['stages']) {
  //     let dealFound = stage['deals'].find((deal) => deal['_id'] == data);
  //     if (dealFound) {
  //       dealData = dealFound;
  //       prevStage = stage['stageId'];
  //     }
  //   }
  //   if (!dealData) return;

  //   // Calculate drop position based on highlighted drop zone
  //   const targetStage = pipeline['stages'].find(s => s['stageId'] === newStage);
  //   let targetPosition = 0;

  //   const dropContainer = evt.currentTarget.querySelector('.drag_row');
  //   if (dropContainer) {
  //     // Find the highlighted drop zone
  //     const highlightedZone = dropContainer.querySelector('.drop-zone.highlight');
  //     if (highlightedZone) {
  //       const position = parseInt(highlightedZone.getAttribute('data-position'));
  //       targetPosition = isNaN(position) ? 0 : position;
  //     } else {
  //       // Fallback to original calculation
  //       if (targetStage && targetStage['deals'].length > 0) {
  //         const rect = dropContainer.getBoundingClientRect();
  //         const dropY = evt.clientY - rect.top;

  //         // Find all deal elements in the target column
  //         const dealElements = Array.from(dropContainer.querySelectorAll('.box-card'));

  //         // Calculate which position to insert at based on drop Y coordinate
  //         for (let i = 0; i < dealElements.length; i++) {
  //           const dealRect = dealElements[i].getBoundingClientRect();
  //           const dealMiddle = dealRect.top + dealRect.height / 2 - rect.top;

  //           if (dropY < dealMiddle) {
  //             targetPosition = i;
  //             break;
  //           }
  //           targetPosition = i + 1;
  //         }
  //       }
  //     }

  //   }

  //   // Clear ALL visual feedback across ALL columns after drop
  //   const allContainers = document.querySelectorAll('.drag_row');
  //   allContainers.forEach(container => {
  //     container.classList.remove('drag-active');
  //     const highlights = container.querySelectorAll('.drop-zone.highlight');
  //     highlights.forEach(zone => zone.classList.remove('highlight'));
  //   });

  //   // Remove global dragging class
  //   document.body.classList.remove('dragging');

  //   // Update the pipeline state with proper positioning
  //   let targetStageDeals = [];
  //   for (let stage of pipeline['stages']) {
  //     if (stage['stageId'] === newStage) {
  //       // Insert at calculated position instead of beginning
  //       stage['deals'].splice(targetPosition, 0, dealData);
  //       targetStageDeals = stage['deals']; // Get the updated array
  //     }
  //     if (stage['stageId'] === prevStage) {
  //       stage['deals'] = stage['deals'].filter((deal) => deal['_id'] !== data);
  //     }
  //   }

  //   // Send the complete deal order for the target stage
  //   const dealPositions = targetStageDeals.map((deal, index) => ({
  //     dealId: deal['_id'],
  //     position: index
  //   }));

  //   dispatch(moveDealStage({
  //     id: data,
  //     stage: newStage,
  //     targetPosition,
  //     dealPositions // Send complete position array
  //   }));
  //   setPipeline({ ...pipeline, stages: pipeline['stages'] });
  // }, [pipeline]);

  const toggleVisibilityBlocks = (stage) => {
    setIsVisibleBlocks((prev) => {
      const newState = { ...prev, [stage]: !prev[stage] };
      ViewStateManager.setPipelineCollapsedStages(newState);
      return newState;
    });
  };

  const handleSelectCheckBox = (dealId) => {
    if (selectItems.includes(dealId)) setSelectItems(selectItems.filter((id) => id !== dealId));
    else setSelectItems([...selectItems, dealId]);
  };

  // Shared drop handler
  const handleDropLogic = (evt, newStage) => {
    evt.preventDefault?.();
    evt['currentTarget']?.['classList']?.remove('dragged-over');

    const data = evt['dataTransfer']?.getData('text/plain');
    if (!data) return;

    let dealData, prevStage;
    for (let stage of pipeline['stages']) {
      let dealFound = stage['deals'].find((deal) => deal['_id'] == data);
      if (dealFound) {
        dealData = dealFound;
        prevStage = stage['stageId'];
      }
    }
    if (!dealData) return;

    // --- Calculate drop position ---
    const targetStage = pipeline['stages'].find(s => s['stageId'] === newStage);
    let targetPosition = 0;

    const dropContainer = evt.currentTarget?.querySelector?.('.drag_row');
    if (dropContainer) {
      const highlightedZone = dropContainer.querySelector('.drop-zone.highlight');
      if (highlightedZone) {
        const position = parseInt(highlightedZone.getAttribute('data-position'));
        targetPosition = isNaN(position) ? 0 : position;
      } else if (targetStage && targetStage['deals'].length > 0) {
        const rect = dropContainer.getBoundingClientRect();
        const dropY = evt.clientY - rect.top;

        const dealElements = Array.from(dropContainer.querySelectorAll('.box-card'));

        for (let i = 0; i < dealElements.length; i++) {
          const dealRect = dealElements[i].getBoundingClientRect();
          const dealMiddle = dealRect.top + dealRect.height / 2 - rect.top;

          if (dropY < dealMiddle) {
            targetPosition = i;
            break;
          }
          targetPosition = i + 1;
        }
      }
    }

    // --- Cleanup visual feedback ---
    const allContainers = document.querySelectorAll('.drag_row');
    allContainers.forEach(container => {
      container.classList.remove('drag-active');
      const highlights = container.querySelectorAll('.drop-zone.highlight');
      highlights.forEach(zone => zone.classList.remove('highlight'));
    });
    document.body.classList.remove('dragging');

    // --- Update pipeline ---
    let targetStageDeals = [];
    for (let stage of pipeline['stages']) {
      if (stage['stageId'] === newStage) {
        stage['deals'].splice(targetPosition, 0, dealData);
        targetStageDeals = stage['deals'];
      }
      if (stage['stageId'] === prevStage) {
        stage['deals'] = stage['deals'].filter((deal) => deal['_id'] !== data);
      }
    }

    const dealPositions = targetStageDeals.map((deal, index) => ({
      dealId: deal['_id'],
      position: index
    }));

    dispatch(moveDealStage({
      id: data,
      stage: newStage,
      targetPosition,
      dealPositions
    }));

    setPipeline({ ...pipeline, stages: pipeline['stages'] });
  };


  // --- Reuse in both events ---
  const onDrop = useCallback((evt, _, newStage) => {
    handleDropLogic(evt, newStage);
  }, [pipeline]);

  // const onDragEnd = useCallback((evt, _, newStage) => {
  //   handleDropLogic(evt, newStage);
  // }, [pipeline]);


  const onDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;

    // ❌ agar koi destination nahi hai
    if (!destination) return;

    // ❌ agar wahi column + wahi index hai
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // pipeline ko clone karna zaroori hai (immutability)
    const newPipeline = { ...pipeline, stages: pipeline.stages.map(stage => ({...stage, deals: [...stage.deals],})),};

    // source aur destination stage find karo
    const sourceStage = newPipeline.stages.find(s => s.stageId === source.droppableId);
    const destStage = newPipeline.stages.find(s => s.stageId === destination.droppableId);

    if (!sourceStage || !destStage) return;

    // 🟢 dragged deal find
    const draggedDeal = sourceStage.deals.find(d => d._id === draggableId);
    if (!draggedDeal) return;

    // Check if destination stage is collapsed (closed)
    const isDestStageCollapsed = isVisibleBlocks[destStage.stageId];

    // 1) remove from source
    sourceStage.deals = sourceStage.deals.filter(d => d._id !== draggableId);

    // 2) insert into destination
    destStage.deals.splice(destination.index, 0, draggedDeal);

    // backend ko bhejne ke liye naya order
    const dealPositions = destStage.deals.map((deal, index) => ({ dealId: deal._id, position: index, }));

    // Update local state immediately for responsive UI
    setPipeline(newPipeline);

    // Also update Redux state immediately so it persists across navigation
    dispatch(setPipelineBoard({...pipelineBoard, stages: newPipeline.stages }));

    // Show notification if deal was moved to a collapsed column
    if (isDestStageCollapsed) {
      const dealTitle = draggedDeal.title || draggedDeal.name || 'Deal';
      const stageName = destStage.stageName || destStage.name || 'Stage';
      
      // Show toast notification
      toast.success(
        `"${dealTitle}" moved to ${stageName}`,
        {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );

      // Temporarily highlight the collapsed column
      setHighlightedStage(destStage.stageId);
      setTimeout(() => {
        setHighlightedStage(null);
      }, 2000); // Remove highlight after 2 seconds
    }

    // redux dispatch - saga will make API call to save to backend
    dispatch( moveDealStage({ id: draggableId, stage: destStage.stageId, targetPosition: destination.index, dealPositions,}));
  }, [pipeline, pipelineBoard, dispatch, isVisibleBlocks]);



  // const renderStageDeals = (stageDeals) => {
  //   const elements = [];
  //   elements.push(
  //     <div key={`drop-zone-0`} className='drop-zone' data-position={0}></div>
  //   );

  //   stageDeals.forEach((deal, index) => {
  //     elements.push(
  //       <div key={`drop-zone-0`} className='drop-zone' data-position={0}></div>
  //     );

  //     stageDeals.forEach((deal, index) => {
  //       elements.push(

  //       );
  //       elements.push(
  //         <div
  //           key={`drop-zone-${index + 1}`}
  //           className="drop-zone"
  //           data-position={index + 1}
  //         ></div>
  //       );
  //     });
  //   });

  //   return elements;
  // };


  return (
    <>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <div className='row'>
          {pipeline && pipeline['stages'] ? pipeline['stages'].map((stage, idx) => (
            <div className={`${isVisibleBlocks[stage['stageId']] ? 'col-2 active' : 'col-2'}`} key={stage.stageId}>
              <Droppable droppableId={stage.stageId} type="DEAL">
                {(provided) => (
                  <div
                    className={`${stage['stageId'].toLowerCase().replace(/\s/g, '-')}-box small-box`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <section className='drag_container'>
                      {isVisibleBlocks[stage['stageId']]
                        ? <div className={`container vertical-button-sec ${highlightedStage === stage['stageId'] ? 'highlighted-column' : ''}`}>
                          <div className='drag_column vertical-mode'>
                            <div className='title-area vertical'>
                              <button className='arrow-btn' onClick={() => toggleVisibilityBlocks(stage['stageId'])}><i className='icon'><svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='0.5' y='0.5' width='14' height='14' rx='7' fill='#FBFBFF' stroke='#4B5563' /><path d='M8.52284 10.06L6 7.53003L8.52284 5' stroke='#4B5563' stroke-linecap='round' stroke-linejoin='round' /></svg></i></button>
                              <h4>{stage['stageName']} <span>{stage['deals'].length}</span></h4>
                            </div>
                            <div className='bottom-area'>
                              <h4>
                                <span>Total:</span> {
                                  stage['deals'].reduce((acc, deal) => {
                                    let num = 0;
                                    if (deal['amount']) num = parseFloat(deal['amount'] || 0);
                                    return acc + num;
                                  }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
                                }
                              </h4>
                            </div>
                          </div>
                        </div>
                        : <div className='container'>
                          <div className='drag_column'>
                            <div className='title-area'>
                              <h4>{stage['stageName']}</h4>
                              <div className='right-btn-area-arrow'>
                                <span>{stage['deals'].length}</span>
                                <button className='arrow-btn' onClick={() => toggleVisibilityBlocks(stage['stageId'])}><i className='icon'><svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='0.5' y='0.5' width='14' height='14' rx='7' fill='#FBFBFF' stroke='#4B5563' /><path d='M8.52284 10.06L6 7.53003L8.52284 5' stroke='#4B5563' stroke-linecap='round' stroke-linejoin='round' /></svg></i></button>
                              </div>

                            </div>
                            <div
                              className='drag_row'
                            >
                              {stage.deals.map((deal, index) => (
                                <Draggable key={deal._id} draggableId={deal._id} index={index}>
                                   {(provided, snapshot) => {
                                     // Merge our tilt transform with react-beautiful-dnd's positioning transform
                                     // Subtle tilt from top left corner when dragging
                                     const style = snapshot.isDragging && provided.draggableProps.style?.transform
                                       ? {
                                           ...provided.draggableProps.style,
                                           // Combine react-beautiful-dnd's transform with subtle rotation from top left
                                           // rotate(3deg) - subtle tilt, no scale to keep it natural
                                           transform: `${provided.draggableProps.style.transform} rotate(3deg)`,
                                           transformOrigin: 'left top', // Tilt from top left corner
                                         }
                                       : provided.draggableProps.style;
                                     
                                     return (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`box-card${snapshot.isDragging ? ' dragging' : ''}`}
                                      style={style}
                                    >
                                      <div className='title-box'>
                                        <span class='w-100' onClick={() => history.push(`/deals/${deal['_id']}`)}>
                                          <h3>{deal['title']}</h3>
                                          <div className='name-owener-area'>
                                            <div className='left-name-area'>
                                              {deal['ownerId']?.['firstName'] &&
                                                <div className='owner-name'>
                                                  <span className='name-area'>{deal['ownerId']?.['firstName']?.charAt(0) || ''}</span>
                                                  {/* <p>{deal['ownerId']?.['firstName']}</p> */}
                                                  <div className='overlaybox-tooltip'>
                                                    <p>{`${deal['ownerId']?.['firstName']} ${deal['ownerId']?.['lastName']}`}</p>
                                                  </div>
                                                </div>
                                              }
                                            </div>
                                            <div className='right-price-area'>
                                              <div className='owner-name'>
                                                {deal['amount'] > 0 && <p>{deal['amount'].toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>}
                                              </div>
                                            </div>
                                          </div>
                                        </span>
                                        <div className={`checkbox-right-area ${selectItems.length > 0 && ' show-checkbox'}`}> <Checkbox checked={selectItems.includes(deal['_id'])} onChange={() => handleSelectCheckBox(deal['_id'])} /></div>
                                      </div>
                                      <span class='w-100' onClick={() => history.push(`/deals/${deal['_id']}`)}>
                                        <div className='detial-box'>
                                          <p>{deal['activityInfo']['subText']}</p>
                                          <div className='user-name-area'>
                                            {/* <span className='name-area'><img src={require('../../static/img/user-deal.png')} alt='' /></span> */}
                                            {deal['contacts'] && deal['contacts'].slice(0, 3).map((contact) => (
                                              <span key={contact['_id'] || contact['firstName']} className='name-area'>
                                                {contact?.['firstName']?.charAt(0) || ''}
                                                <div className='overlaybox-tooltip'>
                                                  <p>{`${contact?.['firstName']} ${contact?.['lastName']}`}</p>
                                                </div>
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        {/* Tags Display */}
                                        {deal['tags'] && deal['tags'].length > 0 && (
                                          <div className='tags-area' style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {deal['tags'].filter(tag => tag != null).slice(0, 3).map((tag, index) => {
                                              // Handle both string and object tags
                                              const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
                                              // Use tag color if available, otherwise default gray
                                              const bgColor = (typeof tag === 'object' && tag?.color) ? tag.color : '#e0e0e0';
                                              const textColor = bgColor === '#e0e0e0' ? '#666' : 'white';

                                              // Skip empty tag names
                                              if (!tagName) return null;

                                              return (
                                                <span
                                                  key={`${tagName}-${index}`}
                                                  style={{
                                                    backgroundColor: bgColor,
                                                    color: textColor,
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontSize: '10px',
                                                    fontWeight: '500',
                                                    display: 'inline-block'
                                                  }}
                                                >
                                                  {tagName}
                                                </span>
                                              );
                                            })}
                                            {deal['tags'].filter(tag => tag != null).length > 3 && (
                                              <span style={{
                                                color: '#666',
                                                fontSize: '10px',
                                                padding: '2px 4px'
                                              }}>
                                                +{deal['tags'].filter(tag => tag != null).length - 3}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </span>
                                      <div className='links-area'>
                                        <ul>
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'note', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M8.6 14V13.4C8.6 11.1373 8.6 10.0059 9.30296 9.30294C10.0059 8.59998 11.1373 8.59998 13.4 8.59998H14M6.8 2H9.2C11.4627 2 12.5941 2 13.297 2.70294C14 3.40589 14 4.53726 14 6.79998V7.04448C14 8.10576 14 8.6364 13.7894 9.10542C13.5787 9.57444 13.1821 9.927 12.3889 10.6321L9.95888 12.7921C9.28964 13.387 8.95502 13.6844 8.5466 13.841L8.5343 13.8457C8.12498 14 7.67726 14 6.78192 14C4.53677 14 3.41419 14 2.71302 13.3071L2.69293 13.287C2 12.5858 2 11.4632 2 9.2181V6.79998C2 4.53726 2 3.40589 2.70294 2.70294C3.40589 2 4.53726 2 6.8 2Z' stroke='#212121' stroke-linejoin='round' /></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Add Note</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'task', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M7.33301 13H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.33301 8.33325H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.33301 3.6665H13.9997' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 3.66659L2.66667 4.33325L4.66667 2.33325' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 8.33333L2.66667 9L4.66667 7' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /><path d='M2 12.9998L2.66667 13.6665L4.66667 11.6665' stroke='#212121' stroke-linecap='round' stroke-linejoin='round' /></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Add Task</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          {/* Call - only show if deal has contacts with phone */}
                                          {deal['contacts']?.some(c => c.phone) && (
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'call', associations: { deals: [deal['_id']], contacts: deal['contacts']?.filter(c => c.phone).map(c => c._id) || [] }, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M13 10.8127C13 11.029 12.9519 11.2514 12.8498 11.4677C12.7476 11.684 12.6154 11.8883 12.4412 12.0806C12.1467 12.4051 11.8222 12.6395 11.4557 12.7897C11.0951 12.9399 10.7046 13.018 10.2839 13.018C9.67101 13.018 9.01602 12.8738 8.32499 12.5794C7.63395 12.2849 6.94291 11.8883 6.25789 11.3896C5.56685 10.8848 4.91187 10.326 4.28693 9.70706C3.668 9.08212 3.10916 8.42714 2.61042 7.74211C2.11768 7.05709 1.72108 6.37206 1.43265 5.69304C1.14422 5.00801 1 4.35303 1 3.72809C1 3.31948 1.07211 2.92889 1.21632 2.56835C1.36054 2.2018 1.58888 1.8653 1.90736 1.56485C2.29194 1.18628 2.71257 1 3.15724 1C3.32549 1 3.49374 1.03605 3.64397 1.10816C3.8002 1.18027 3.93841 1.28843 4.04657 1.44467L5.44066 3.40961C5.54882 3.55984 5.62694 3.69805 5.68102 3.83025C5.7351 3.95643 5.76515 4.08262 5.76515 4.1968C5.76515 4.34101 5.72308 4.48523 5.63896 4.62343C5.56084 4.76164 5.44667 4.90586 5.30245 5.05007L4.84577 5.52479C4.77967 5.59089 4.74962 5.669 4.74962 5.76515C4.74962 5.81322 4.75563 5.85528 4.76765 5.90335C4.78568 5.95143 4.80371 5.98748 4.81572 6.02353C4.92389 6.22183 5.11017 6.48022 5.37456 6.79269C5.64497 7.10516 5.9334 7.42364 6.24587 7.74211C6.57036 8.06059 6.88282 8.35503 7.2013 8.62544C7.51377 8.88983 7.77216 9.0701 7.97646 9.17827C8.00651 9.19028 8.04256 9.20831 8.08463 9.22634C8.1327 9.24437 8.18077 9.25037 8.23485 9.25037C8.33701 9.25037 8.41512 9.21432 8.48122 9.14822L8.93791 8.69755C9.08813 8.54732 9.23235 8.43315 9.37056 8.36104C9.50876 8.27691 9.64697 8.23485 9.7972 8.23485C9.91137 8.23485 10.0315 8.25889 10.1637 8.31297C10.2959 8.36705 10.4342 8.44517 10.5844 8.54732L12.5734 9.95944C12.7296 10.0676 12.8378 10.1938 12.9039 10.344C12.9639 10.4942 13 10.6445 13 10.8127Z' stroke='#212121' stroke-miterlimit='10' /></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Call</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          )}
                                          {/* SMS - only show if deal has contacts with phone */}
                                          {deal['contacts']?.some(c => c.phone) && (
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'sms', associations: { deals: [deal['_id']], contacts: deal['contacts']?.filter(c => c.phone).map(c => c._id) || [] }, objectData: deal, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z' stroke='#212121' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Send SMS</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          )}
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'mail', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M11.9997 2.66675H3.99967C2.52691 2.66675 1.33301 3.86065 1.33301 5.33341V10.6667C1.33301 12.1395 2.52691 13.3334 3.99967 13.3334H11.9997C13.4724 13.3334 14.6663 12.1395 14.6663 10.6667V5.33341C14.6663 3.86065 13.4724 2.66675 11.9997 2.66675Z' stroke='#212121' /><path d='M3.33301 6L5.39651 7.76871C6.63872 8.83344 7.25981 9.36584 7.99967 9.36584C8.73954 9.36584 9.36061 8.83344 10.6028 7.76871L12.6663 6' stroke='#212121' stroke-linejoin='round' /><path d='M5.33301 8L3.33301 10' stroke='#212121' stroke-linejoin='round' /><path d='M10.666 8L12.666 10' stroke='#212121' stroke-linejoin='round' /></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Send Email</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          <li>
                                            <button onClick={() => {dispatch(setActiveActivityBox({ activityBox: 'schedule', associations: { deals: [deal['_id']] }, source: 'pipeline' })); setIsSideViewModal(false);}}>
                                              <i className='icon'>
                                                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5.33301 1.33325V3.33325' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M10.667 1.33325V3.33325' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M2.33301 6.05981H13.6663' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M14 5.66659V11.3333C14 13.3333 13 14.6666 10.6667 14.6666H5.33333C3 14.6666 2 13.3333 2 11.3333V5.66659C2 3.66659 3 2.33325 5.33333 2.33325H10.6667C13 2.33325 14 3.66659 14 5.66659Z' stroke='#212121' stroke-miterlimit='10' stroke-linecap='round' stroke-linejoin='round' /><path d='M7.99707 9.1333H8.00307' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /><path d='M5.5293 9.1333H5.53528' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /><path d='M5.5293 11.1333H5.53528' stroke='#212121' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /></svg>
                                                <div className='overlaybox-tooltip'>
                                                  <p>Add Meeting</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                          <li>
                                            <button onClick={() => setIsSideViewModal(deal)}>
                                              <i className='icon view-icon'>
                                                <ViewIcon />
                                                <div className='overlaybox-tooltip'>
                                                  <p>Quick View</p>
                                                </div>
                                              </i>
                                            </button>
                                          </li>
                                        </ul>
                                      </div>
                                    </div>
                                    );
                                  }}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              {/* {renderStageDeals(stage['deals'])} */}
                            </div>
                            <div className='bottom-area'>
                              <h4>
                                <span>Total:</span> {
                                  stage['deals'].reduce((acc, deal) => {
                                    let num = 0;
                                    if (deal['amount']) num = parseFloat(deal['amount'] || 0);
                                    return acc + num;
                                  }, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
                                }
                              </h4>
                            </div>
                          </div>
                        </div>
                      }
                    </section>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

            </div>
          )) : loading ? <div style={{textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'calc(100vh - 165px)' }}><PlanAnimation /></div> : null}
        </div>  
      </DragDropContext>

    </>
  )
}

export default PipelineView;